import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  createSubMaterial,
  deleteSubMaterial,
  getMaterialsCatalog,
} from 'services/adminMaterialsService';
import { ParentMaterialCatalogDto } from 'types/api.types';

type ToastSeverity = 'success' | 'error';

interface AddChildDraft {
  name: string;
  description: string;
  points: string;
  isAdding: boolean;
}

const emptyAddChildDraft: AddChildDraft = {
  name: '',
  description: '',
  points: '',
  isAdding: false,
};

const MaterialsCatalog: React.FC = () => {
  const [materials, setMaterials] = useState<ParentMaterialCatalogDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [childDraftsByParent, setChildDraftsByParent] = useState<Record<number, AddChildDraft>>({});
  const [isDeletingChildById, setIsDeletingChildById] = useState<Record<number, boolean>>({});
  const [toast, setToast] = useState<{ message: string; severity: ToastSeverity } | null>(null);

  const showErrorToast = (message: string) => setToast({ message, severity: 'error' });
  const showSuccessToast = (message: string) => setToast({ message, severity: 'success' });

  const loadCatalog = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    const result = await getMaterialsCatalog();

    if (!result) {
      setMaterials([]);
      setErrorMessage('Unable to load materials catalog.');
      setIsLoading(false);
      return;
    }

    setMaterials(result.materials);
    setChildDraftsByParent(
      result.materials.reduce(
        (acc, material) => ({
          ...acc,
          [material.id]: { ...emptyAddChildDraft },
        }),
        {},
      ),
    );
    setIsLoading(false);
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  const handleAddChild = async (parentId: number) => {
    const parent = materials.find((material) => material.id === parentId);
    if (!parent) return;

    const draft = childDraftsByParent[parentId] ?? emptyAddChildDraft;
    const name = draft.name.trim();
    if (!name) {
      showErrorToast('Enter a sub-material name before adding.');
      return;
    }

    const pointsValue = draft.points.trim();
    const parsedPoints = pointsValue ? Number.parseInt(pointsValue, 10) : undefined;
    if (pointsValue && (Number.isNaN(parsedPoints) || (parsedPoints ?? 0) < 0)) {
      showErrorToast('Points must be a non-negative number.');
      return;
    }

    setChildDraftsByParent((current) => ({
      ...current,
      [parentId]: { ...draft, isAdding: true },
    }));

    const description = draft.description.trim();
    const created = await createSubMaterial(parentId, {
      name,
      description: description || undefined,
      points: parsedPoints,
    });

    setChildDraftsByParent((current) => ({
      ...current,
      [parentId]: { ...emptyAddChildDraft },
    }));

    if (!created) {
      showErrorToast('Unable to add sub-material.');
      return;
    }

    setMaterials((current) =>
      current.map((material) =>
        material.id === parentId
          ? {
              ...material,
              children: [...material.children, created].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
              ),
            }
          : material,
      ),
    );
    showSuccessToast('Sub-material added.');
  };

  const handleDeleteChild = async (parentId: number, childId: number) => {
    setIsDeletingChildById((current) => ({ ...current, [childId]: true }));
    const result = await deleteSubMaterial(childId);
    setIsDeletingChildById((current) => ({ ...current, [childId]: false }));

    if (!result.success) {
      showErrorToast(result.errorMessage ?? 'Unable to remove sub-material.');
      return;
    }

    setMaterials((current) =>
      current.map((material) =>
        material.id === parentId
          ? {
              ...material,
              children: material.children.filter((child) => child.id !== childId),
            }
          : material,
      ),
    );
    showSuccessToast('Sub-material removed.');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', p: { xs: 1, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Materials catalog
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Add or remove sub-materials (child types) under each top-level material.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Removing a sub-material hides it from the catalog. Past deposits that used it are unchanged.
      </Alert>

      {errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : materials.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>No top-level materials are configured yet.</Typography>
        </Paper>
      ) : (
        materials.map((material) => {
          const draft = childDraftsByParent[material.id] ?? emptyAddChildDraft;
          return (
            <Accordion key={material.id} defaultExpanded={material.children.length > 0} sx={{ mb: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {material.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {material.children.length} sub-material{material.children.length === 1 ? '' : 's'}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Sub-materials
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {material.children.map((child) => (
                      <TableRow key={child.id}>
                        <TableCell>{child.name}</TableCell>
                        <TableCell>{child.description}</TableCell>
                        <TableCell>{child.points}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleDeleteChild(material.id, child.id)}
                            disabled={isDeletingChildById[child.id]}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ py: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Sub-material name"
                          value={draft.name}
                          onChange={(event) =>
                            setChildDraftsByParent((current) => ({
                              ...current,
                              [material.id]: {
                                ...(current[material.id] ?? emptyAddChildDraft),
                                name: event.target.value,
                              },
                            }))
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Description"
                          value={draft.description}
                          onChange={(event) =>
                            setChildDraftsByParent((current) => ({
                              ...current,
                              [material.id]: {
                                ...(current[material.id] ?? emptyAddChildDraft),
                                description: event.target.value,
                              },
                            }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleAddChild(material.id);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder={`Default (${material.points})`}
                          value={draft.points}
                          onChange={(event) =>
                            setChildDraftsByParent((current) => ({
                              ...current,
                              [material.id]: {
                                ...(current[material.id] ?? emptyAddChildDraft),
                                points: event.target.value,
                              },
                            }))
                          }
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddChild(material.id)}
                          disabled={draft.isAdding}
                        >
                          {draft.isAdding ? 'Adding...' : 'Add'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast(null)} severity={toast?.severity ?? 'success'} sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaterialsCatalog;
